import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import { usePartnerById } from '@rhg/hooks/use-partner';
import {
    TransitionDestinationPartner,
    useTransitionPartner,
} from '@rhg/hooks/use-transition-partner';
import { logger } from '@rhg/libs/logger';
import { daysAgo, getUtcDateForMDY } from '@rhg/utils/date-time';
import { useTransitionDialogDismissal } from '../use-transition-dialog-dismissal';
import { useUpgradeDialog } from '../use-upgrade-dialog';

export interface UseBuyUpEligibilityResult {
    isLoading: boolean;
    /**
     * Whether eligibility has settled on an answer. Unlike `isLoading` this
     * never goes back to false, so consumers gating on it can't be torn down
     * by a mid-session re-sync of the underlying flag.
     */
    isFetched: boolean;
    isEligible: boolean;
    destinationPartner: TransitionDestinationPartner | null;
    deadline: Date | null;
    isPastDeadline: boolean;
    willShowBanner: boolean;
    /**
     * Whether the upgrade takeover wants the screen — due on the 7-day cadence
     * or force-opened. Read by anything that must yield to it, not just the
     * dialog itself, so page-level exclusions stay with the renderer.
     */
    willShowDialog: boolean;
    dismissDialog: () => void;
}

export function useBuyUpEligibility(): UseBuyUpEligibilityResult {
    const { activeCircle, isFetched: isActiveCircleFetched } =
        useActiveCircle();
    const currentPartnerId = activeCircle?.sponsor?.partnerId;
    // Fail closed if the backend ever sends canUpgrade without a partnerId —
    // otherwise usePartnerById(undefined) below stays disabled and
    // isPartnerFetched never resolves, leaving isLoading stuck true.
    const canUpgrade = Boolean(activeCircle?.canUpgrade && currentPartnerId);

    const { partner, isFetched: isPartnerFetched } = usePartnerById(
        canUpgrade ? currentPartnerId : undefined
    );

    const { hasLostSponsorship } = useMostRecentSponsorship();

    const {
        isDismissalDue,
        isFetched: isDismissalFetched,
        dismiss,
    } = useTransitionDialogDismissal();

    const {
        isLoading: isFlagLoading,
        isFetched: isFlagFetched,
        destinationPartner,
    } = useTransitionPartner(partner);

    const { isUpgradeDialogOpen } = useUpgradeDialog();

    // SDK types every field on CirclePartnerTransition as optional, but the
    // backend only ever populates this object with all fields set together
    // (see CirclePartnerTransition's own doc comment) — the optionality is
    // generator-conservatism, not a real partial-population case.
    const endsAt = partner?.partnerTransition?.endsAt;
    const deadline = endsAt ? getUtcDateForMDY(endsAt) : null;
    // Local-calendar-day comparison (not a raw timestamp compare) so the
    // cutoff lines up with the local-midnight deadline above — matches the
    // same reasoning behind useTransitionDialogDismissal's use of daysAgo.
    const isPastDeadline = deadline ? daysAgo(deadline) > 0 : false;

    const isLoading =
        isFlagLoading ||
        !isActiveCircleFetched ||
        (canUpgrade && !isPartnerFetched) ||
        !isDismissalFetched;

    // Everything past the active circle only matters to a user who can
    // upgrade at all, so a non-upgradeable user is settled as soon as the
    // circle lands. That keeps consumers gating on this from hanging forever
    // when the Amplitude client fails to initialise and its flag never
    // reports fetched (EVE-14436).
    const isFetched =
        isActiveCircleFetched &&
        (!canUpgrade ||
            (isPartnerFetched && isFlagFetched && isDismissalFetched));

    // Eligibility no longer ends at the deadline — once it passes, the
    // banner/dialog keep recurring (via the same 7-day dismissal cadence)
    // until the sponsorship is actually lost or the user upgrades.
    const isEligible = Boolean(
        canUpgrade && destinationPartner && !hasLostSponsorship
    );

    const isDialogDue = isEligible && isDismissalFetched && isDismissalDue;
    // Both the Home banner CTA and the /upgrade deeplink drive `udo`,
    // force-opening the takeover regardless of the 7-day dismissal gating.
    const willShowDialog = isDialogDue || (isEligible && isUpgradeDialogOpen);

    return {
        isLoading,
        isFetched,
        isEligible,
        destinationPartner,
        deadline,
        isPastDeadline,
        willShowBanner: isEligible,
        willShowDialog,
        dismissDialog: () => {
            dismiss().catch((err) =>
                logger.error(
                    'transitionPartner.dismiss.failed',
                    {},
                    err as Error
                )
            );
        },
    };
}
